import axios from 'axios';
import api, { clearAuthFailed } from './api';
import {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  User
} from '../utils/types';
import {
  API_ENDPOINTS,
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_KEY
} from '../utils/constants';
import { storage } from '../utils/storage';
import { hasAuthFailed } from './authFailedState';

// Register new user
export const register = async (data: RegisterRequest): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>(API_ENDPOINTS.AUTH.REGISTER, data);

  // Tokens are now stored in httpOnly cookies (set by backend)
  // Only store user data in storage for UI purposes
  if (response.data.user) {
    await storage.set(USER_KEY, JSON.stringify(response.data.user));
  }

  return response.data;
};

// Login user
export const login = async (data: LoginRequest): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>(API_ENDPOINTS.AUTH.LOGIN, data);

  // Tokens are now stored in httpOnly cookies (set by backend)
  // Only store user data in storage for UI purposes
  if (response.data.user) {
    await storage.set(USER_KEY, JSON.stringify(response.data.user));

    // CRITICAL: Clear terminal auth_failed state on successful login before
    // returning. If this isn't awaited, callers can navigate and fire the
    // next authenticated request while hasAuthFailed() still reads true,
    // which the response interceptor short-circuits into an immediate reject.
    await clearAuthFailed();
  }

  return response.data;
};

// Logout user
export const logout = async (): Promise<void> => {
  try {
    // Call backend to clear httpOnly cookies
    await api.post(API_ENDPOINTS.AUTH.LOGOUT);
  } catch (error) {
    // Even if logout fails, clear local data
    console.error('Logout error:', error);
  } finally {
    // Clear user data from storage (works on both web and iOS)
    await Promise.all([
      storage.remove(USER_KEY),
      storage.remove(AUTH_TOKEN_KEY), // Legacy key
      storage.remove(REFRESH_TOKEN_KEY), // Legacy key
    ]);
  }
};

// Get current user from localStorage (synchronous for backward compatibility)
// Note: On iOS, this may return stale data immediately after login/logout
// For critical auth checks, use isAuthenticated() or check the backend
export const getCurrentUser = (): User | null => {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;

  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

/**
 * A health_only account (e.g. a spouse) is confined to the Health module.
 * The backend enforces this; the frontend just hides the rest and lands them
 * on /health.
 */
export const isHealthOnly = (): boolean => getCurrentUser()?.account_type === 'health_only';

/**
 * Check if user is authenticated.
 * Checks both localStorage user data AND presence of auth cookies.
 */
export const isAuthenticated = (): boolean => {
  // Check terminal auth failure state (uses in-memory cache for sync access)
  if (hasAuthFailed()) {
    return false;
  }

  // Check if user data exists
  const hasUserData = !!localStorage.getItem(USER_KEY);
  if (!hasUserData) {
    return false;
  }

  // Note: We can't directly check httpOnly cookies from JS, but we can
  // check if the document.cookie string is completely empty (indicating
  // all cookies were cleared). In normal operation, there should be some cookies.
  // This is a heuristic - the real validation happens on API calls.

  return true;
};

/**
 * Verify auth state by checking the server.
 * Use this on app foreground to proactively detect stale auth.
 *
 * Note: If the access token is expired but refresh token is valid,
 * the API interceptor will automatically refresh the token and retry.
 * This function will return true in that case (after the refresh).
 */
export const verifyAuthState = async (): Promise<boolean> => {
  console.log('[Auth] verifyAuthState called');

  if (!isAuthenticated()) {
    console.log('[Auth] verifyAuthState: isAuthenticated() returned false');
    return false;
  }

  try {
    console.log('[Auth] verifyAuthState: Making /users/me request...');
    // Use the api instance to make a lightweight auth check
    // The interceptor will handle token refresh if needed
    const apiModule = await import('./api');
    await apiModule.default.get('/api/v1/users/me');
    console.log('[Auth] verifyAuthState: Request succeeded, auth is valid');
    return true;
  } catch (err) {
    // If we get here, either:
    // 1. The refresh token is also expired/invalid
    // 2. There was a network error
    // 3. The user was logged out
    let errorStatus: number | string = 'NO_RESPONSE';
    let errorCode = 'UNKNOWN';

    if (axios.isAxiosError(err)) {
      errorStatus = err.response?.status || 'NO_RESPONSE';
      const data = err.response?.data as Record<string, unknown> | undefined;
      errorCode = (data?.code as string) || (data?.error_code as string) || (data?.error as string) || 'UNKNOWN';
    }

    console.log(`[Auth] verifyAuthState failed: status=${errorStatus}, code=${errorCode}`);
    return false;
  }
};

// Get auth token - DEPRECATED: Tokens now in httpOnly cookies
export const getAuthToken = (): string | null => {
  console.warn('getAuthToken is deprecated - tokens are now in httpOnly cookies');
  return null;
};

// Get refresh token - DEPRECATED: Tokens now in httpOnly cookies
export const getRefreshToken = (): string | null => {
  console.warn('getRefreshToken is deprecated - tokens are now in httpOnly cookies');
  return null;
};

// Refresh access token - DEPRECATED: Now handled automatically by api.ts interceptor
// Tokens are in httpOnly cookies and refreshed automatically
export const refreshAccessToken = async (): Promise<string | null> => {
  console.warn('refreshAccessToken is deprecated - token refresh is now automatic via cookies');
  try {
    // Call refresh endpoint - new token will be set in cookie by backend
    await api.post(API_ENDPOINTS.AUTH.REFRESH_TOKEN);
    return 'token-in-cookie'; // Return placeholder since we can't access the cookie
  } catch (error) {
    // If refresh fails, logout
    await logout();
    return null;
  }
};

